/**
 * ChatStream — renders an array of chat messages.
 *
 * User messages render as plain bubbles. Assistant messages render through
 * MarkdownRenderer, which mounts inline `:::widget[X]:::` directives via
 * the web widget registry. Tool-call chips render compactly above the
 * assistant message body during streaming.
 *
 * Each new message animates in via motion (--duration-base + ease-out from
 * tokens.css). AnimatePresence's initial={false} suppresses the animation
 * on first mount so a fresh page load doesn't strobe every prior message.
 * useReducedMotion() respects the OS preference.
 *
 * Scroll-anchor: when a NEW user message is appended, scroll it to the top
 * of the viewport so the streaming assistant reply fills the page below it
 * (mirrors ChatGPT / Claude.ai). We watch the last user message id, not
 * messages.length — so streaming tokens that mutate the assistant message
 * do NOT re-trigger the scroll.
 */
import { useEffect, useMemo, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { MarkdownRenderer } from '../canvas/markdown-renderer';
import type { ChatMessage, ToolCallChip } from '../../lib/use-chat';

export interface ChatStreamProps {
  messages: ReadonlyArray<ChatMessage>;
  isStreaming: boolean;
  onPromptClick?: (prompt: string) => void;
}

// Mirrors --duration-base (220ms) and --ease-out from tokens.css. Lift to a
// shared util once a third surface uses motion.
const ENTER_TRANSITION = {
  duration: 0.22,
  ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
};

export function ChatStream({ messages, isStreaming, onPromptClick }: ChatStreamProps) {
  const lastMessageRole = messages[messages.length - 1]?.role;
  const reduceMotion = useReducedMotion();

  const lastUserMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return messages[i].id;
    }
    return null;
  }, [messages]);

  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!lastUserMessageId) return;
    const el = containerRef.current?.querySelector(
      `[data-message-id="${lastUserMessageId}"]`,
    ) as HTMLElement | null;
    el?.scrollIntoView({
      block: 'start',
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }, [lastUserMessageId, reduceMotion]);

  return (
    <div
      ref={containerRef}
      role="log"
      aria-live="polite"
      aria-label="Conversation"
      className="flex-1 flex flex-col gap-4 px-4 py-6 overflow-y-auto"
    >
      <AnimatePresence initial={false}>
        {messages.map((msg) => (
          <motion.div
            key={msg.id}
            data-message-id={msg.id}
            initial={reduceMotion ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={reduceMotion ? { duration: 0 } : ENTER_TRANSITION}
            className={msg.role === 'user' ? 'self-end max-w-[80%]' : 'self-start max-w-[90%]'}
          >
            <MessageBubble message={msg} onPromptClick={onPromptClick} />
          </motion.div>
        ))}
      </AnimatePresence>
      {isStreaming && lastMessageRole === 'user' && (
        <div className="self-start text-sm text-foreground-mute italic">
          Agent thinking…
        </div>
      )}
      {/* Scroll headroom — guarantees the last user message can reach the
          top of the viewport via scrollIntoView, even when the assistant
          reply hasn't arrived yet. Without this, send() (which appends
          only the user message in its first setState) finds zero content
          below the new message and the scroll is a no-op. */}
      {messages.length > 0 && (
        <div aria-hidden="true" className="shrink-0 min-h-[50vh]" />
      )}
    </div>
  );
}

function MessageBubble({
  message,
  onPromptClick,
}: {
  message: ChatMessage;
  onPromptClick?: (prompt: string) => void;
}) {
  if (message.role === 'user') {
    return (
      <div className="rounded-lg bg-primary/10 px-3 py-2 text-sm">
        {message.content}
      </div>
    );
  }
  // Assistant message — pass per-message streaming status to enable
  // streamdown's animated caret only on the actively-streaming bubble.
  // Past assistant messages render statically; the current one animates.
  return (
    <>
      {message.toolCalls && message.toolCalls.length > 0 && (
        <ToolCallChips chips={message.toolCalls} />
      )}
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <MarkdownRenderer
          onPromptClick={onPromptClick}
          isAnimating={message.status === 'streaming'}
        >
          {message.content}
        </MarkdownRenderer>
      </div>
    </>
  );
}

function ToolCallChips({ chips }: { chips: ReadonlyArray<ToolCallChip> }) {
  return (
    <div className="flex flex-col gap-1 mb-2">
      {chips.map((chip) => (
        <div
          key={chip.id}
          className="text-xs text-foreground-mute flex gap-2 items-center"
        >
          <span aria-hidden>
            {chip.status === 'running' ? '⊙' : chip.status === 'done' ? '✓' : '✗'}
          </span>
          <span>{chip.summary || chip.toolName}</span>
          {chip.durationMs !== undefined && (
            <span className="text-[10px] opacity-60">{chip.durationMs}ms</span>
          )}
        </div>
      ))}
    </div>
  );
}
